import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useMutationWithError, usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

import {
  type AdminPeriod,
  type AdminTopValue,
  type AdminTransactionTransferSummary,
  type AdminUserTransactionTransferSummary,
} from "@/modules/recognition/admin/types";

export type PositionList = Paginate<AdminPeriod<true>>;

export const periods = createQueryKeys("admin-recognition-periods", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => ({
    queryKey: [id],
    contextQueries: {
      point_bags: null,
      top_receivers: null,
      top_senders: null,
      received_summary: null,
      sent_summary: null,
      transactions_summary: null,
      top_values: null,
    },
  }),
});

const include = "values,point_bags,specialUsers";

export const fetchPeriodsQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...periods.all(page, search),
    queryFn: () =>
      adminApi
        .get<PositionList>(`/recognition/periods`, {
          params: { page, include, ...search },
        })
        .then((response) => response.data),
  });

export const useFetchPeriods = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchPeriodsQueryOptions(page, search));

export const fetchPeriodQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id),
    queryFn: () =>
      adminApi
        .get<AdminPeriod<true, true>>(`/recognition/periods/${id}`, {
          params: { include },
        })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchPeriod = (id?: number) =>
  useQuery(fetchPeriodQueryOptions(id));

export interface PeriodRequest {
  name: string;
  start_date: string;
  end_date: string;
  points: number;
  is_repeatable: boolean;
  values: number[];
  point_bags: number[];
  special_users: Array<{ user_id: number; special_points?: number }>;
  special_points: number | null;
  special_point_value: number | null;
  max_points_per_user: number | null;
}

export const useCreatePeriod = () => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: PeriodRequest) =>
      adminApi
        .post("/recognition/periods/", data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: periods.all._def,
      });
    },
  });
};

export const useUpdatePeriod = (id: number) => {
  const queryClient = useQueryClient();
  return useMutationWithError({
    mutationFn: (data: PeriodRequest) =>
      adminApi
        .put(`/recognition/periods/${id}`, data)
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: periods.all._def,
      });
      queryClient.invalidateQueries(periods.one(id));
    },
  });
};

export const useCreateOrUpdatePeriod = (id: number | undefined) => {
  const create = useCreatePeriod();
  const update = useUpdatePeriod(id!);
  return id ? update : create;
};

export const useDeletePeriod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`recognition/periods/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: periods.all._def,
      });
      queryClient.invalidateQueries(periods.one(id));
    },
  });
};

export const fetchPeriodReceivedSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id)._ctx.received_summary,
    queryFn: () =>
      adminApi
        .get<AdminUserTransactionTransferSummary[]>(
          `/recognition/periods/${id}/receivers/summary`,
          {
            params: { include: "admin.position" },
          },
        )
        .then((response) => response.data),
  });

export const useFetchPeriodReceivedSummary = (id?: number) =>
  useQuery(fetchPeriodReceivedSummaryQueryOptions(id));

export const fetchPeriodSentSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id)._ctx.sent_summary,
    queryFn: () =>
      adminApi
        .get<AdminUserTransactionTransferSummary[]>(
          `/recognition/periods/${id}/senders/summary`,
          {
            params: { include: "admin" },
          },
        )
        .then((response) => response.data),
  });

export const useFetchPeriodSentSummary = (id?: number) =>
  useQuery(fetchPeriodSentSummaryQueryOptions(id));

export const fetchPeriodTopValuesQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id)._ctx.top_values,
    queryFn: () =>
      adminApi
        .get<AdminTopValue[]>(`/recognition/periods/${id}/values/top`)
        .then((response) => response.data),
  });

export const useFetchPeriodTopValues = (id?: number) =>
  useQuery(fetchPeriodTopValuesQueryOptions(id));

export const fetchPeriodTransactionSummaryQueryOptions = (id?: number) =>
  queryOptions({
    ...periods.one(id)._ctx.transactions_summary,
    queryFn: () =>
      adminApi
        .get<
          AdminTransactionTransferSummary[]
        >(`/recognition/periods/${id}/transactions/summary`)
        .then((response) => response.data),
  });

export const useFetchPeriodTransactionSummary = (id?: number) =>
  useQuery(fetchPeriodTransactionSummaryQueryOptions(id));
